/**
 * GLDN PDP Tall Sticky Info Column
 *
 * The default Horizon sticky behavior on the right-side product details
 * column pins the TOP of the column to the viewport top. When the column
 * is taller than the viewport (typical on short laptop screens, or when
 * Material/Gemstone/Chain Length/Custom Length/Add-ons all expand the
 * column), the Add to Cart button sits below the fold and the user has to
 * scroll through every product image in the left column before the button
 * becomes reachable.
 *
 * Switch to a "bottom-stick" mode whenever the column is taller than the
 * available viewport (viewport height minus the sticky-header offset).
 * Pure CSS sticky only honors one of top/bottom, so we set `top` to a
 * computed negative offset such that the column's bottom edge anchors at
 * the viewport bottom while scrolling. Effect:
 *
 *   - Short column (fits in viewport): top = --sticky-header-offset.
 *     Behaves exactly like the default Horizon top-stick.
 *
 *   - Tall column (doesn't fit): top = viewportHeight - columnHeight.
 *     The column scrolls naturally with the page until its bottom (ATC)
 *     reaches the viewport bottom, then pins there while the left media
 *     column continues to scroll. Once the containing grid row ends, the
 *     page resumes normal scroll. Matches the legacy site behavior.
 *
 * Recomputed on resize and whenever the column content changes
 * (variant swaps, custom-length input revealing, special-request toggle,
 * etc.). MutationObserver covers content-driven height changes without
 * coupling to specific event names.
 */
(function () {
  if (window.__gldnPdpTallStickyInstalled) return;
  window.__gldnPdpTallStickyInstalled = true;

  function update(el) {
    if (!el || !el.isConnected) return;
    /* Only run when the element is the desktop sticky variant. The class
     * is present on desktop and absent on mobile so we don't need a
     * separate media query check — but read CSS sticky to be safe. */
    var cs = getComputedStyle(el);
    if (cs.position !== 'sticky') {
      el.style.removeProperty('top');
      return;
    }

    var docCS = getComputedStyle(document.documentElement);
    var headerOffsetRaw = docCS.getPropertyValue('--sticky-header-offset').trim();
    var headerOffset = parseFloat(headerOffsetRaw) || 0;

    var vp = window.innerHeight;
    var colHeight = el.offsetHeight;

    /* If the column fits in the available viewport, leave the default
     * top-stick (Horizon's `top: var(--sticky-header-offset, 0)`). If it
     * overflows, override `top` with a negative offset such that when
     * sticky engages, the column's bottom edge sits exactly at the
     * viewport bottom. CSS sticky pins the element at this offset only
     * once natural scrolling brings the element there — before that, the
     * column scrolls 1:1 with the page. After the containing grid row
     * ends, sticky releases and the column scrolls past normally.
     *
     * (We use top: <negative> rather than bottom: 0 because the default
     * Horizon CSS sets top: var(...) on the same selector. An inline
     * bottom: 0 without overriding the inherited top can leave both top
     * and bottom set, which keeps the top-pin engaged and prevents the
     * scroll-together-then-pin behavior.) */
    if (colHeight + headerOffset <= vp) {
      el.style.removeProperty('top');
    } else {
      el.style.top = (vp - colHeight) + 'px';
    }
  }

  function init(root) {
    var els = (root || document).querySelectorAll('.product-details.sticky-content--desktop');
    els.forEach(function (el) {
      update(el);

      /* React to content-driven height changes (variant swap, custom-length
       * reveal, special request toggle, etc.). Throttled via rAF. */
      var pending = false;
      var ro = new ResizeObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          update(el);
        });
      });
      ro.observe(el);
    });
  }

  function recomputeAll() {
    document
      .querySelectorAll('.product-details.sticky-content--desktop')
      .forEach(update);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Viewport resize: re-evaluate short-vs-tall mode. Debounced via rAF. */
  var resizePending = false;
  window.addEventListener('resize', function () {
    if (resizePending) return;
    resizePending = true;
    requestAnimationFrame(function () {
      resizePending = false;
      recomputeAll();
    });
  });

  /* Theme editor: re-init when this section re-renders. */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) {
      init(event.target);
    }
  });
})();
